// Centralised content for Zolvix Technologies website.
import {
  Code2, Layers, Smartphone, Building2, BrainCircuit, Workflow, Cloud,
  Cpu, Palette, LineChart, Users, Boxes, Headset, BarChart3, Bot,
  Database, ShoppingBag, Stethoscope, GraduationCap, Briefcase, HardHat,
  Factory, Drill, Wrench, Truck, ServerCog, ShieldCheck,
} from "lucide-react";

export const LOGOS = {
  zolvix: "/zolvix-logo.png",
  zolvixMark: "/zolvix-mark.png",
  clan: "/clan-logo.png",
  clanMark: "/clan-mark.png",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Solutions", to: "/solutions" },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const STATS = [
  { label: "Global Hubs · Mumbai & Dubai", value: 2, suffix: "" },
  { label: "Industries Served", value: 10, suffix: "+" },
  { label: "In-house Senior Engineering", value: 100, suffix: "%" },
  { label: "Global Delivery Capability", value: 24, suffix: "/7" },
];

export const SERVICES = [
  { icon: Code2, title: "Custom Software Development", blurb: "Bespoke platforms engineered around your operating model — built to scale with your business." },
  { icon: Layers, title: "SaaS Product Development", blurb: "From validated MVPs to multi-tenant SaaS — designed for product-market fit and recurring revenue." },
  { icon: Smartphone, title: "Mobile Application Development", blurb: "Native and cross-platform mobile apps with a focus on performance, polish and conversion." },
  { icon: Building2, title: "Enterprise Application Development", blurb: "Mission-critical enterprise systems with strong security, observability and governance." },
  { icon: BrainCircuit, title: "AI & Automation Solutions", blurb: "LLM-powered assistants, copilots and intelligent workflows that compound team productivity." },
  { icon: Workflow, title: "ERP & Business Process Automation", blurb: "Connect departments, automate paperwork and unlock real-time operational intelligence." },
  { icon: Cloud, title: "Cloud Engineering", blurb: "Cloud-native architecture on AWS, Azure and GCP — secure, observable and cost-optimised." },
  { icon: Cpu, title: "Product Engineering", blurb: "Dedicated pods that own discovery, design, build, release and growth as one team." },
  { icon: Palette, title: "UI / UX Design", blurb: "Research-led product design that converts ambiguity into measurable user outcomes." },
  { icon: LineChart, title: "Digital Transformation Consulting", blurb: "Strategy, architecture and operating-model design to modernise your digital core." },
];

export const SOLUTIONS = [
  { icon: Users, title: "CRM Platforms", blurb: "Pipeline visibility, automation and customer 360°." },
  { icon: Boxes, title: "ERP Systems", blurb: "Finance, inventory, procurement and operations in one fabric." },
  { icon: Briefcase, title: "HRMS Solutions", blurb: "People, payroll, performance and engagement." },
  { icon: ServerCog, title: "Vendor Management Systems", blurb: "Supplier lifecycle, RFQs, contracts and scorecards." },
  { icon: Headset, title: "Customer Portals", blurb: "Self-service hubs that reduce support load and lift NPS." },
  { icon: Smartphone, title: "Mobile Applications", blurb: "Customer and field-force apps with offline-first UX." },
  { icon: Bot, title: "AI Assistants", blurb: "Domain-tuned copilots powered by retrieval and tools." },
  { icon: Workflow, title: "Workflow Automation Platforms", blurb: "Composable automation that connects every system you run." },
  { icon: BarChart3, title: "Analytics Dashboards", blurb: "Real-time KPIs, anomaly detection and decision intelligence." },
];

export const INDUSTRIES = [
  { icon: Factory, label: "Manufacturing" },
  { icon: Wrench, label: "Engineering" },
  { icon: HardHat, label: "Construction" },
  { icon: Building2, label: "Infrastructure" },
  { icon: Drill, label: "Oil & Gas" },
  { icon: Truck, label: "Logistics" },
  { icon: ShoppingBag, label: "Retail" },
  { icon: Stethoscope, label: "Healthcare" },
  { icon: GraduationCap, label: "Education" },
  { icon: Briefcase, label: "Professional Services" },
];

export const PROCESS = [
  { step: "01", title: "Discover", body: "Deep listening, business outcomes mapping and feasibility framing." },
  { step: "02", title: "Design", body: "User research, IA, design systems and clickable prototypes." },
  { step: "03", title: "Develop", body: "Cross-functional pods shipping production-grade code every week." },
  { step: "04", title: "Test", body: "Automated, manual and security testing baked into the pipeline." },
  { step: "05", title: "Deploy", body: "Zero-downtime releases with full observability and rollback." },
  { step: "06", title: "Scale", body: "Performance, growth experiments and continuous optimisation." },
];

export const CASE_STUDIES = [
  {
    title: "Manufacturing ERP Platform",
    industry: "Manufacturing",
    summary: "End-to-end visibility from inward materials to finished spools — real-time dashboards for piping projects.",
    metrics: [["38%", "Order cycle ↓"], ["7", "Plants live"], ["12wk", "MVP"]],
    image: "https://customer-assets.emergentagent.com/job_zolvix-preview/artifacts/rrx4u3o6_ERP%20Project.png",
    tags: ["ERP", "Piping", "Real-time"],
    fit: "contain",
  },
  {
    title: "AI Workflow Automation System",
    industry: "Professional Services",
    summary: "LLM-powered intake, triage and document drafting for a global firm.",
    metrics: [["62%", "Manual work ↓"], ["3.4×", "Throughput"], ["GPT-class", "Models"]],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=70",
    tags: ["AI", "Automation", "Workflow"],
  },
  {
    title: "Vendor Management Portal",
    industry: "Infrastructure",
    summary: "Onboarding, RFQs, contracts and scorecards across 1,200+ vendors.",
    metrics: [["1.2k+", "Vendors"], ["48%", "Cycle ↓"], ["SAP", "Integrated"]],
    image: "https://customer-assets.emergentagent.com/job_zolvix-preview/artifacts/gyt57ruw_Vendor%20Management%20Portal%20.png",
    tags: ["Procurement", "Portal", "Integrations"],
    fit: "contain",
  },
  {
    title: "Customer Self-Service Platform",
    industry: "Retail",
    summary: "Modern portal with AI assistant — reduced support tickets by 41%.",
    metrics: [["41%", "Tickets ↓"], ["+18", "NPS"], ["99.99%", "Uptime"]],
    image: "https://customer-assets.emergentagent.com/job_zolvix-preview/artifacts/3tiegj05_Customer%20Self%20service%20portal%20.png",
    tags: ["Customer Portal", "AI", "SaaS"],
    fit: "contain",
  },
  {
    title: "Mobile Commerce Application",
    industry: "Retail",
    summary: "Native mobile commerce with personalised search and checkout.",
    metrics: [["2.4×", "Conversion"], ["4.8★", "App rating"], ["0.8s", "TTI"]],
    image: "https://customer-assets.emergentagent.com/job_zolvix-preview/artifacts/g4c9khi0_Mobile%20Commerce%20Application.png",
    tags: ["Mobile", "Commerce", "Personalisation"],
    fit: "contain",
  },
];

export const TECH_STACK = {
  Frontend: ["React", "Next.js", "Vue"],
  Backend: ["Node.js", "Python", ".NET"],
  Databases: ["PostgreSQL", "MySQL", "MongoDB"],
  Cloud: ["AWS", "Azure", "Google Cloud"],
  AI: ["OpenAI", "LangChain", "Vector DBs"],
};

export const WHY_ZOLVIX = [
  { icon: BarChart3, title: "Business-First Approach", body: "Every engineering decision is tied to a measurable business outcome." },
  { icon: Building2, title: "Enterprise Architecture", body: "Patterns proven across regulated, high-load enterprise environments." },
  { icon: Cloud, title: "Scalable Platforms", body: "Built to handle 10× growth without re-architecting." },
  { icon: Workflow, title: "Agile Delivery", body: "Weekly demos, transparent burndown, no surprises." },
  { icon: ShieldCheck, title: "Long-Term Support", body: "Stable squads stay with your product post-launch." },
  { icon: Users, title: "Dedicated Teams", body: "Senior, full-stack pods that operate as your in-house team." },
];

export const TESTIMONIALS = [
  {
    quote: "Zolvix's ERP platform has transformed the way we manage our piping projects. The real-time dashboards for materials, manpower, fit-up, welding, and NDT progress provide complete visibility and control, enabling faster and more informed project decisions.",
    name: "Jineesh",
    role: "CEO, GBM E&T",
    initials: "JN",
    accent: "from-cyan to-royal",
  },
  {
    quote: "Their AI automation pod shipped in weeks what our internal team had been planning for a year.",
    name: "Aisha Al-Mansoori",
    role: "Director of Digital, Dubai-based Holding",
    initials: "AA",
    accent: "from-royal to-cyan",
  },
  {
    quote: "Senior engineers, real product thinking, and a delivery cadence we can actually trust.",
    name: "Vikram Iyer",
    role: "CTO, B2B SaaS Startup",
    initials: "VI",
    accent: "from-cyan-300 to-royal-500",
  },
];

export const ROLES = [
  { title: "Senior Full-Stack Engineer", location: "Mumbai · Hybrid", type: "Full-time" },
  { title: "Product Designer", location: "Mumbai · Hybrid", type: "Full-time" },
  { title: "AI / ML Engineer", location: "Remote · India / UAE", type: "Full-time" },
  { title: "DevOps Engineer", location: "Mumbai · Hybrid", type: "Full-time" },
  { title: "Business Development Manager", location: "Dubai · On-site", type: "Full-time" },
  { title: "Engineering Manager", location: "Mumbai · Hybrid", type: "Full-time" },
];

export const BLOG_POSTS = [
  { category: "AI", title: "Practical patterns for shipping LLM features in production", excerpt: "Beyond prompts: retrieval, evals, guardrails and the platform layer that makes it stick.", date: "Dec 2025", read: "8 min", image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=70" },
  { category: "SaaS", title: "The architecture decisions that decide your SaaS unit economics", excerpt: "From multi-tenancy to billing primitives — choices that show up on your P&L.", date: "Nov 2025", read: "11 min", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=70" },
  { category: "Digital Transformation", title: "Why most ERP rollouts fail — and the operating model that actually works", excerpt: "A field guide for COOs and CIOs leading multi-year transformation programmes.", date: "Nov 2025", read: "9 min", image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=70" },
  { category: "Automation", title: "From ticket queues to autonomous workflows", excerpt: "Designing automations that handle 80% of repetitive work — and escalate the rest gracefully.", date: "Oct 2025", read: "7 min", image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=1200&q=70" },
  { category: "Product Development", title: "Discovery sprints that actually de-risk your roadmap", excerpt: "A 2-week pattern we run with every new product engagement — and the artefacts it produces.", date: "Oct 2025", read: "6 min", image: "https://images.unsplash.com/photo-1581091215367-9b6c00b3039f?auto=format&fit=crop&w=1200&q=70" },
  { category: "Technology", title: "Choosing between AWS, Azure and GCP in 2026", excerpt: "A pragmatic, vendor-neutral checklist for enterprise architects.", date: "Sep 2025", read: "10 min", image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=70" },
];

export const LOCATIONS = [
  { city: "Mumbai", country: "India", role: "Corporate & Delivery Center", lat: 19.07, lng: 72.87, image: "https://images.pexels.com/photos/5414582/pexels-photo-5414582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { city: "Dubai", country: "UAE", role: "International Business Development", lat: 25.2, lng: 55.27, image: "https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?auto=format&fit=crop&w=1200&q=70" },
];
